/**
 * The Page window: the page of the node last selected, like a wiki article or a note in a
 * vault. Read mode draws its Markdown, `[[Name]]` links leading to other nodes (or making one
 * when no node has the name); Edit (or a double click) writes it, `[[` offering the names of the
 * environment's nodes. What is written is sent when leaving the editor (Done, Ctrl+Enter,
 * Escape, clicking elsewhere, or selecting another node): one undo per writing session. Where
 * the environment writes documents (the `document` view, Writing's), the page shown is the node's
 * document and Edit opens it in the document editor (made from the node's template when it has
 * none yet). Below: the nodes it mentions and the pages that mention it (backlinks).
 */
import { For, Show, createEffect, createMemo, createSignal, on, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { MarkdownView } from "../../shared/Markdown";
import { toast } from "../../shared/toast";
import { Group, NodeRef, useKindLabel } from "../../shared/ui";
import { fileShown as shownFile, openFile } from "../../state/assets";
import { formatSize } from "../../shared/assets";
import { documentOf, templateFor } from "../../state/documents";

/** How many names `[[` offers at most. */
const SUGGESTIONS = 8;

/** The name being typed after an open `[[` just before the caret, if any. */
export function typedMention(text: string, caret: number): string | null {
  const open = /\[\[([^\]\n|[]*)$/.exec(text.slice(0, caret));
  return open ? open[1]! : null;
}

/** Whether the `[[` open before the caret is a file's (`![[`). */
export function typedFile(text: string, caret: number): boolean {
  return /!\[\[[^\]\n|[]*$/.test(text.slice(0, caret));
}

/** One name offered after `[[`: a node's, or after `![[` a file's. */
interface Offer {
  /** What `data-suggest` says (the node's id, or the file's path). */
  key: string;
  /** What is written. */
  name: string;
  /** Shown beside it (the node's kind, the file's size). */
  note: string;
}

/** The window. */
export function PageWindow(): JSX.Element {
  const { pages, graph, assets, presets, documents, view, wm, access } = useApp();
  const kindLabel = useKindLabel();
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal("");
  /** The node the draft belongs to (the selection may move while editing). */
  let draftOf: string | null = null;
  const [typing, setTyping] = createSignal<string | null>(null);
  const [picked, setPicked] = createSignal(0);
  let area: HTMLTextAreaElement | undefined;

  /** Sends the draft, if any, and leaves the editor. */
  const finish = async () => {
    const id = draftOf;
    const text = draft();
    draftOf = null;
    setEditing(false);
    setTyping(null);
    if (id && !(await pages.save(id, text))) toast("The page was not saved.", "error");
  };
  /** Whether pages are written as documents here (the environment shows the document view). */
  const writesDocuments = () => view.views().includes("document");
  /** What the page says: the node's document's words when it has one, else its Markdown page. */
  const text = (node: Node) => (documentOf(node) ? (documents.textOf(node) ?? "") : (node.page ?? ""));
  /** Opens the node's document in the document view, making it first from the node's template. */
  const editDocument = async (node: Node) => {
    if (!documentOf(node)) {
      if (!access.can("editor")) return void toast("Editors and owners write documents.", "error");
      const template = templateFor(documents.templates(), node.kind);
      if (!template) return void toast("No template to write the document with: turn on an environment that brings some, such as Writing.", "error");
      if (!(await documents.create(template.id, node.name, node.id))) return;
    }
    pages.show(node.id);
    view.setMode("document");
    wm.open("graph");
  };
  const edit = () => {
    const node = pages.current();
    if (!node) return;
    if (writesDocuments()) return void editDocument(node);
    draftOf = node.id;
    setDraft(node.page ?? "");
    setEditing(true);
    queueMicrotask(() => area?.focus());
  };
  // Another node selected while writing: what was written goes to its own node first.
  createEffect(on(() => pages.current()?.id, (id) => draftOf && draftOf !== id && void finish(), { defer: true }));

  /** Whether a file's name is being typed (`![[`). */
  const [typingFile, setTypingFile] = createSignal(false);
  /** The names `[[` offers for what is typed (the project's files after `![[`). */
  const offered = createMemo((): Offer[] => {
    const typed = typing();
    if (typed === null) return [];
    const q = typed.trim().toLowerCase();
    if (typingFile()) {
      const files = assets.source("project")?.listing.files ?? [];
      return files.filter((f) => f.path.toLowerCase().includes(q)).slice(0, SUGGESTIONS).map((f) => ({ key: f.path, name: f.path, note: formatSize(f.size) }));
    }
    const self = pages.current()?.id;
    return graph.state.graph.nodes
      .filter((n: Node) => n.id !== self && n.name.toLowerCase().includes(q))
      .slice(0, SUGGESTIONS)
      .map((n) => ({ key: n.id, name: n.name, note: kindLabel(n) }));
  });
  /** Puts `name]]` in place of what was typed after `[[`. */
  const complete = (name: string) => {
    if (!area) return;
    const caret = area.selectionStart;
    const typed = typing() ?? "";
    const text = draft();
    const next = `${text.slice(0, caret - typed.length)}${name}]]${text.slice(caret).replace(/^\]\]/, "")}`;
    setDraft(next);
    setTyping(null);
    const at = caret - typed.length + name.length + 2;
    queueMicrotask(() => area?.setSelectionRange(at, at));
  };
  const onInput = (e: InputEvent & { currentTarget: HTMLTextAreaElement }) => {
    setDraft(e.currentTarget.value);
    setTyping(typedMention(e.currentTarget.value, e.currentTarget.selectionStart));
    setTypingFile(typedFile(e.currentTarget.value, e.currentTarget.selectionStart));
    setPicked(0);
  };
  const onKey = (e: KeyboardEvent) => {
    const list = offered();
    if (list.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setPicked((i) => (i + (e.key === "ArrowDown" ? 1 : list.length - 1)) % list.length);
    } else if (list.length && (e.key === "Enter" || e.key === "Tab")) {
      e.preventDefault();
      complete(list[Math.min(picked(), list.length - 1)]!.name);
    } else if (e.key === "Escape" && typing() !== null) {
      setTyping(null);
    } else if (e.key === "Escape" || (e.key === "Enter" && (e.ctrlKey || e.metaKey))) {
      e.preventDefault();
      void finish();
    }
    e.stopPropagation();
  };

  /** The nodes of `ids` that are in the graph shown. */
  const nodesOf = (ids: string[] | undefined) => (ids ?? []).map((id) => graph.state.graph.nodes.find((n) => n.id === id)).filter((n): n is Node => !!n);
  const mentions = () => nodesOf(pages.index().mentions.get(pages.current()?.id ?? ""));
  const backlinks = () => nodesOf(pages.index().backlinks.get(pages.current()?.id ?? ""));
  const missing = () => pages.index().missing.get(pages.current()?.id ?? "") ?? [];

  return (
    <div class="page-window" id="page">
      <Show when={pages.current()} fallback={<p class="muted empty-page">Select a node to read or write its page.</p>}>
        {(node) => (
          <>
            <header class="page-head">
              <div>
                <span class="muted small">{kindLabel(node())}</span>
                <h2 id="page-title">{node().name}</h2>
              </div>
              <Show
                when={editing()}
                fallback={
                  <button type="button" class="btn" id="page-edit" onClick={edit}>
                    Edit
                  </button>
                }
              >
                <button type="button" class="btn primary" id="page-done" onMouseDown={(e) => e.preventDefault()} onClick={() => void finish()}>
                  Done
                </button>
              </Show>
            </header>
            <Show
              when={editing()}
              fallback={
                <div class="page-read" onDblClick={edit}>
                  <Show
                    when={text(node())}
                    fallback={
                      <p class="muted">
                        Nothing written yet. <button type="button" class="linkish" onClick={edit}>{writesDocuments() ? "Write its document" : "Write the page"}</button>; <code>[[Name]]</code> mentions another node.
                      </p>
                    }
                  >
                    <MarkdownView text={text(node())} resolve={(name) => !!pages.resolve(name)} onLink={(name) => pages.follow(name)} file={(path) => shownFile(assets, path)} onFile={(path) => openFile(assets, presets, path)} />
                  </Show>
                </div>
              }
            >
              <div class="page-write">
                <textarea
                  ref={area}
                  id="page-text"
                  class="input"
                  value={draft()}
                  spellcheck
                  placeholder={"# Heading\nWrite freely. [[Name]] mentions another node."}
                  onInput={onInput}
                  onKeyDown={onKey}
                  onBlur={() => void finish()}
                />
                <Show when={offered().length}>
                  <ul class="page-suggest" id="page-suggest" role="listbox">
                    <For each={offered()}>
                      {(n, i) => (
                        <li>
                          <button type="button" role="option" aria-selected={i() === picked()} data-suggest={n.key} onMouseDown={(e) => { e.preventDefault(); complete(n.name); }}>
                            {n.name} <em>{n.note}</em>
                          </button>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
                <p class="muted small">Markdown: # heading, **bold**, *italic*, - list, &gt; quote. [[Name]] mentions a node, ![[file.png]] shows one of the project's files. Ctrl+Enter or Escape when done.</p>
              </div>
            </Show>
            <div class="page-links">
              <Group title="Mentions" count={mentions().length}>
                <For each={mentions()}>{(n) => <NodeRef node={n} onOpen={() => pages.show(n.id)} />}</For>
              </Group>
              <div id="page-backlinks">
                <Group title="Mentioned in" count={backlinks().length}>
                  <For each={backlinks()}>{(n) => <NodeRef node={n} onOpen={() => pages.show(n.id)} />}</For>
                </Group>
              </div>
              <Show when={missing().length}>
                <section class="grp">
                  <h3>
                    Not written yet <span>{missing().length}</span>
                  </h3>
                  <div class="page-missing">
                    <For each={missing()}>
                      {(name) => (
                        <button type="button" class="wiki missing" data-missing={name} onClick={() => pages.follow(name)}>
                          {name}
                        </button>
                      )}
                    </For>
                  </div>
                </section>
              </Show>
            </div>
          </>
        )}
      </Show>
    </div>
  );
}
